/*
 * Shared (app + Cloud Functions): the server feed item contract.
 * The app runs sanitizeServerItem on every item it receives; the Functions
 * run the same code before an item can be submitted or published, so
 * anything the server publishes is guaranteed to render. See feed.md.
 */
import { toAppPath, domainOf, parseBookPage } from './links.js';
import {
  EMBED_TYPES,
  PROVIDER_NAMES,
  parseEmbed,
  youtubeThumbnail,
} from './embeds.js';

export const SUPPORTED_VERSION = 1;

export const ITEM_TYPES = [
  'audio',
  'letter',
  'article',
  'quote',
  'mindmap',
  'reading',
  'bookmark',
  'announcement',
  // Server-only types (see feed.md)
  'youtube',
  'tweet',
  'instagram',
  'link',
  'tip',
  'funfact',
  'event',
];

// Types curators can create (the others come from the app's own sections).
export const CURATOR_TYPES = [
  'announcement',
  'youtube',
  'tweet',
  'instagram',
  'link',
  'tip',
  'funfact',
  'article',
  'quote',
  'event',
  'bookmark',
  'audio',
];

// Curator notes are Markdown (links, bold, lists); cards show them as plain
// text and "Read more" opens the full note.
export const NOTE_MAX = 3000;

export const AUDIENCES = ['all', 'signedIn', 'signedOut'];
export const LANGS = ['en', 'hi', 'mr'];
export const EVENT_MODES = ['online', 'in_person', 'hybrid'];

// An event stays in the feed (marked "Ended") this long after it finishes,
// unless the curator set an explicit endAt.
export const EVENT_GRACE_MS = 12 * 60 * 60 * 1000;
export const MAX_EVENT_MINUTES = 7 * 24 * 60;

// Fields a server item may carry (anything else is dropped on save).
export const ITEM_FIELDS = [
  'type',
  'url',
  'title',
  'summary',
  'note',
  'kicker',
  'link',
  'section',
  'date',
  'lang',
  'media',
  'siteName',
  'author',
  'weight',
  'pinned',
  // Shown in the "Today on BAWS.in" box on wide screens instead of the stream.
  'today',
  // Announcements only: also added once to every reader's bell (notices.js).
  'notify',
  'startAt',
  'endAt',
  'audience',
  // Events
  'eventStart',
  'durationMinutes',
  'eventMode',
  'location',
  'joinUrl',
  'organizer',
  // Shared bookmarks: highlight rectangles on the page (see sanitizeHighlight)
  'highlight',
];

export const MAX_HIGHLIGHT_RECTS = 50;
const RECT_KEYS = [
  'topPercent',
  'leftPercent',
  'widthPercent',
  'heightPercent',
];

/**
 * Highlight rectangles as the reader stores them (percentages of the PDF
 * page, so they draw the same for everyone). Invalid rectangles are dropped;
 * returns undefined when none are left.
 */
export function sanitizeHighlight(rects) {
  if (!Array.isArray(rects)) return undefined;
  const out = rects
    .slice(0, MAX_HIGHLIGHT_RECTS)
    .map((r) => {
      if (!r || typeof r !== 'object') return null;
      const rect = {};
      for (const k of RECT_KEYS) {
        const v = Number(r[k]);
        if (!Number.isFinite(v) || v < 0 || v > 100) return null;
        rect[k] = Math.round(v * 1000) / 1000;
      }
      return rect;
    })
    .filter(Boolean);
  return out.length ? out : undefined;
}

const str = (v, max = 500) =>
  typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : undefined;

// In-app paths stay as router links; only https URLs may leave the app.
export const safeLink = (link) => {
  const appPath = toAppPath(link);
  if (appPath) return appPath;
  if (typeof link === 'string' && /^https:\/\//i.test(link)) return link;
  return null;
};

const safeImage = (url) =>
  typeof url === 'string' && /^(https:\/\/|\/)/i.test(url) ? url : undefined;

// Width / height of an image or video (e.g. a 4:3 YouTube video).
const ratio = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0.4 && n <= 3
    ? Math.round(n * 1000) / 1000
    : undefined;
};

const time = (v) => {
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
};

const httpsUrl = (v) =>
  typeof v === 'string' && /^https:\/\/\S+$/i.test(v.trim())
    ? v.trim()
    : undefined;

/**
 * Event details: { start, end (ms), durationMinutes, mode, location,
 * joinUrl, organizer }, or null when the start time is missing/invalid.
 */
export function eventDetails(raw) {
  const start = time(raw?.eventStart);
  if (start == null) return null;
  const minutes = Number(raw.durationMinutes);
  const durationMinutes =
    Number.isFinite(minutes) && minutes > 0 && minutes <= MAX_EVENT_MINUTES
      ? Math.round(minutes)
      : 60;
  const joinUrl = httpsUrl(raw.joinUrl);
  return {
    start,
    end: start + durationMinutes * 60000,
    durationMinutes,
    mode: EVENT_MODES.includes(raw.eventMode)
      ? raw.eventMode
      : joinUrl
        ? 'online'
        : 'in_person',
    location: str(raw.location, 200),
    joinUrl,
    organizer: str(raw.organizer, 80),
  };
}

/**
 * Validates one server item. Returns null for anything malformed, so a bad
 * entry in feed.json drops that card instead of breaking the feed.
 */
export function sanitizeServerItem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = str(raw.id, 120);
  let type = raw.type;
  if (!id || !ITEM_TYPES.includes(type)) return null;

  // youtube / tweet / instagram need a post URL from that platform. If it
  // doesn't parse (e.g. a profile page), show it as a plain link card.
  const url = typeof raw.url === 'string' ? raw.url.trim() : undefined;
  let embed;
  if (EMBED_TYPES[type]) {
    embed = parseEmbed(url);
    if (!embed || embed.provider !== EMBED_TYPES[type]) {
      embed = undefined;
      type = 'link';
    }
  }

  // A shared bookmark points at one book page (readers can save it).
  const bookPage = type === 'bookmark' ? parseBookPage(raw.link) : null;
  if (type === 'bookmark' && !bookPage) return null;

  let event;
  if (type === 'event') {
    event = eventDetails(raw);
    // Events always carry an image (the card is designed around it).
    if (!event || !safeImage(raw.media?.thumbnail)) return null;
  }

  // Events may have no page at all (in person, no sign-up link).
  const link =
    safeLink(raw.link || url || event?.joinUrl) || (event ? '/feed' : null);
  const fallbackTitle = embed
    ? `Post on ${PROVIDER_NAMES[embed.provider]}`
    : type === 'link' && link
      ? domainOf(link) || link
      : undefined;
  const title = str(raw.title, 300) || fallbackTitle || bookPage?.label;
  if (!title || !link) return null;

  const date =
    typeof raw.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.date)
      ? raw.date
      : undefined;
  const thumbnail =
    safeImage(raw.media?.thumbnail) ||
    (embed?.provider === 'youtube' ? youtubeThumbnail(embed.id) : undefined);

  return {
    id: `server:${id}`,
    type,
    title,
    link,
    kicker: str(raw.kicker, 80) || bookPage?.label,
    summary: str(raw.summary, type === 'bookmark' ? 2000 : 600),
    // Curator comment ("Note from the BAWS.in Team"), Markdown.
    note: str(raw.note, NOTE_MAX),
    bookPage: bookPage || undefined,
    highlight: bookPage ? sanitizeHighlight(raw.highlight) : undefined,
    // Link / embed preview details.
    siteName: str(raw.siteName, 60),
    author: str(raw.author, 80),
    embed,
    event,
    date,
    addedAt: time(raw.addedAt) ?? time(raw.startAt) ?? undefined,
    lang: LANGS.includes(raw.lang) ? raw.lang : undefined,
    media:
      thumbnail || raw.media?.duration
        ? {
            thumbnail,
            duration: str(raw.media?.duration, 20),
            ratio: ratio(raw.media?.ratio),
          }
        : undefined,
    weight:
      typeof raw.weight === 'number' && raw.weight >= 0 && raw.weight <= 10
        ? raw.weight
        : undefined,
    pinned: raw.pinned === true,
    today: raw.today === true,
    notify: type === 'announcement' && raw.notify === true,
    // Set by publishing: curators' order within a published batch, and when.
    rank: Number.isInteger(raw.rank) && raw.rank >= 0 ? raw.rank : undefined,
    publishedAt: time(raw.publishedAt) ?? undefined,
    // Server-only controls, applied by the server provider.
    section: str(raw.section, 40),
    startAt: time(raw.startAt),
    endAt: time(raw.endAt) ?? (event ? event.end + EVENT_GRACE_MS : null),
    audience: AUDIENCES.includes(raw.audience) ? raw.audience : 'all',
  };
}

/** Human-readable reasons an item can't be published yet (empty = OK). */
export function itemProblems(raw) {
  const problems = [];
  if (!raw || !ITEM_TYPES.includes(raw.type)) problems.push('Choose a type.');
  const embedType = raw && EMBED_TYPES[raw.type];
  if (embedType) {
    const embed = parseEmbed(raw.url);
    if (!raw.url) problems.push('Add the post URL.');
    else if (!embed || embed.provider !== embedType) {
      problems.push(
        `The URL is not a ${PROVIDER_NAMES[embedType]} post; it will show as a link card.`
      );
    }
  }
  if (raw?.type === 'link' && !raw.url && !raw.link)
    problems.push('Add a URL.');
  if (
    raw &&
    !embedType &&
    !['link', 'bookmark'].includes(raw.type) &&
    !str(raw.title)
  ) {
    problems.push('Add a title.');
  }
  if (raw?.type === 'bookmark' && !parseBookPage(raw.link)) {
    problems.push(
      'A bookmark links to one book page, e.g. /books/baws/EN/Volume_01/pdf/38.'
    );
  }
  if (typeof raw?.note === 'string' && raw.note.length > NOTE_MAX) {
    problems.push(`The curator note is too long (max ${NOTE_MAX} characters).`);
  }
  if (raw?.type === 'event') {
    if (time(raw.eventStart) == null)
      problems.push('Add the start date and time.');
    if (!safeImage(raw.media?.thumbnail))
      problems.push('Add an image (https://…).');
    if (raw.joinUrl && !httpsUrl(raw.joinUrl))
      problems.push('The join link must start with https://');
    const m = Number(raw.durationMinutes);
    if (
      raw.durationMinutes !== undefined &&
      (!Number.isFinite(m) || m <= 0 || m > MAX_EVENT_MINUTES)
    ) {
      problems.push('Duration must be between 1 minute and 7 days.');
    }
  } else if (raw && !safeLink(raw.link || raw.url)) {
    problems.push(
      'Add a link: a baws.in page, an in-app path (/…) or an https:// URL.'
    );
  }
  if (raw?.startAt && raw?.endAt && time(raw.endAt) <= time(raw.startAt)) {
    problems.push('End date must be after the start date.');
  }
  return problems;
}

export function sanitizeConfig(raw) {
  const config = { disabledSources: [], sourceWeights: {}, labels: {} };
  if (!raw || typeof raw !== 'object') return config;
  if (Array.isArray(raw.disabledSources)) {
    config.disabledSources = raw.disabledSources.filter(
      (s) => typeof s === 'string'
    );
  }
  Object.entries(raw.sourceWeights || {}).forEach(([k, v]) => {
    if (typeof v === 'number' && v >= 0 && v <= 10) {
      config.sourceWeights[k] = v;
    }
  });
  Object.entries(raw.labels || {}).forEach(([k, v]) => {
    const label = str(v, 40);
    if (label) config.labels[k] = label;
  });
  return config;
}

/** Keeps only known item fields (used when curators save an item). */
export function pickItemFields(raw) {
  const out = {};
  ITEM_FIELDS.forEach((k) => {
    if (raw?.[k] !== undefined && raw[k] !== null && raw[k] !== '') {
      out[k] = raw[k];
    }
  });
  if (out.media) {
    const media = {};
    if (str(out.media.thumbnail)) media.thumbnail = out.media.thumbnail.trim();
    if (str(out.media.duration)) media.duration = out.media.duration.trim();
    if (ratio(out.media.ratio)) media.ratio = ratio(out.media.ratio);
    if (Object.keys(media).length) out.media = media;
    else delete out.media;
  }
  return out;
}
